import { ClientsTypes } from '../../../api/models/common';

/**
 * Ticket artwork per industry. Three optimised WebP sizes each (400 / 512 /
 * 859 px wide, 1.66:1) under public/hero.
 *
 * Which one an account gets:
 *   1. `webapp.config.hero-image-url` — an explicit per-account image, when set.
 *   2. `webapp.config.hero-industry` — 'brokers' | 'travel' | 'homestead'.
 *   3. Client type: BRAND360 (UWM mortgage brokers) → brokers; everyone else → travel.
 */
export type HeroIndustry = 'brokers' | 'travel' | 'homestead';

export const HERO_INDUSTRIES: readonly HeroIndustry[] = ['brokers', 'travel', 'homestead'];

export interface HeroArtwork {
  /** Telemetry: where the artwork came from. */
  source: 'account' | 'industry';
  industry: HeroIndustry | null;
  src: string;
  srcSet?: string;
  sizes?: string;
}

const industryArtwork = (industry: HeroIndustry): HeroArtwork => ({
  source: 'industry',
  industry,
  src: `/hero/${industry}-512.webp`,
  srcSet: `/hero/${industry}-400.webp 400w, /hero/${industry}-512.webp 512w, /hero/${industry}-859.webp 859w`,
  // The frame is the ticket width minus its padding: ≤ ~450 CSS px.
  sizes: '(max-width: 540px) calc(100vw - 80px), 450px',
});

export const industryForClient = (clientType?: string | null): HeroIndustry =>
  clientType === ClientsTypes.BRAND360 ? 'brokers' : 'travel';

export const getHeroArtwork = (opts: {
  clientType?: string | null;
  heroImageUrl?: string | null;
  heroIndustry?: string | null;
}): HeroArtwork => {
  if (opts.heroImageUrl) {
    return { source: 'account', industry: null, src: opts.heroImageUrl };
  }
  const override = HERO_INDUSTRIES.find((i) => i === opts.heroIndustry);
  return industryArtwork(override ?? industryForClient(opts.clientType));
};
